import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForm = ({ selectedType, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    preferredContact: 'whatsapp',
    message: '',
    newsletter: false,
    terms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    { value: 'living', label: 'Sala de Estar' },
    { value: 'dining', label: 'Sala de Jantar' },
    { value: 'bedroom', label: 'Quarto' },
    { value: 'kitchen', label: 'Cozinha' },
    { value: 'office', label: 'Escritório' },
    { value: 'complete', label: 'Casa Completa' },
    { value: 'commercial', label: 'Projeto Comercial' },
    { value: 'other', label: 'Outro' }
  ];

  const budgetRanges = [
    { value: '10-25', label: 'R$ 10.000 - R$ 25.000' },
    { value: '25-50', label: 'R$ 25.000 - R$ 50.000' },
    { value: '50-100', label: 'R$ 50.000 - R$ 100.000' },
    { value: '100-200', label: 'R$ 100.000 - R$ 200.000' },
    { value: '200+', label: 'Acima de R$ 200.000' },
    { value: 'discuss', label: 'Prefiro discutir pessoalmente' }
  ];

  const timelineOptions = [
    { value: '1-2', label: '1-2 meses' },
    { value: '3-4', label: '3-4 meses' },
    { value: '5-6', label: '5-6 meses' },
    { value: '6+', label: 'Mais de 6 meses' },
    { value: 'flexible', label: 'Flexível' }
  ];

  const contactMethods = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'phone', label: 'Telefone' },
    { value: 'email', label: 'E-mail' },
    { value: 'any', label: 'Qualquer um' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!formData?.projectType) {
      newErrors.projectType = 'Tipo de projeto é obrigatório';
    }

    if (!formData?.terms) {
      newErrors.terms = 'Você deve aceitar os termos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      onSubmit({ ...formData, consultationType: selectedType });
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeTitle = () => {
    const types = {
      showroom: 'Visita ao Showroom',
      home: 'Consultoria Domiciliar',
      virtual: 'Consultoria Virtual'
    };
    return types?.[selectedType] || 'Consultoria';
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-primary mb-4">
            Solicitar {getTypeTitle()}
          </h2>
          <p className="text-xl text-text-secondary">
            Preencha os dados abaixo e entraremos em contato em até 15 minutos
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-premium p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informações Pessoais */}
            <div className="space-y-6">
              <h3 className="font-playfair text-xl font-semibold text-primary flex items-center">
                <Icon name="User" size={20} className="mr-2" />
                Informações Pessoais
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Nome Completo"
                  type="text"
                  placeholder="Seu nome completo"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  required
                />
                
                <Input
                  label="E-mail"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Telefone/WhatsApp"
                  type="tel"
                  placeholder="(62) 99326-3202"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  required
                />

                <Select
                  label="Método de Contato Preferido"
                  options={contactMethods}
                  value={formData?.preferredContact}
                  onChange={(value) => handleInputChange('preferredContact', value)}
                  placeholder="Selecione"
                />
              </div>
            </div>

            {/* Detalhes do Projeto */}
            <div className="space-y-6">
              <h3 className="font-playfair text-xl font-semibold text-primary flex items-center">
                <Icon name="Briefcase" size={20} className="mr-2" />
                Detalhes do Projeto
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <Select
                  label="Tipo de Projeto"
                  options={projectTypes}
                  value={formData?.projectType}
                  onChange={(value) => handleInputChange('projectType', value)}
                  error={errors?.projectType}
                  placeholder="Selecione o tipo"
                  required
                />

                <Select
                  label="Orçamento Estimado"
                  options={budgetRanges}
                  value={formData?.budget}
                  onChange={(value) => handleInputChange('budget', value)}
                  placeholder="Selecione a faixa"
                />
              </div>

              <Select
                label="Prazo Desejado"
                options={timelineOptions}
                value={formData?.timeline}
                onChange={(value) => handleInputChange('timeline', value)}
                placeholder="Quando gostaria de finalizar?"
              />

              <Input
                label="Mensagem Adicional"
                type="text"
                placeholder="Conte-nos mais sobre seu projeto, estilo preferido, necessidades específicas..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                description="Opcional - Ajude-nos a entender melhor sua visão"
              />
            </div>

            {/* Termos e Newsletter */}
            <div className="space-y-4 pt-6 border-t border-border">
              <Checkbox
                label="Aceito receber newsletter com dicas de decoração e novidades"
                checked={formData?.newsletter}
                onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
              />

              <Checkbox
                label="Li e aceito os termos de uso e política de privacidade"
                checked={formData?.terms}
                onChange={(e) => handleInputChange('terms', e?.target?.checked)}
                error={errors?.terms}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                className="bg-gradient-forest hover:shadow-forest"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar Consultoria'}
              </Button>
              
              <p className="text-sm text-text-secondary text-center mt-4">
                Responderemos em até 15 minutos durante horário comercial
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;